import React, { useEffect, useRef, useState } from "react";
import { Menu, Table, Tooltip } from "antd";
import styles from "./styles.module.less";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import TextArea from "antd/lib/input/TextArea";
import { PaperMachineQualitySelect } from "../pageStyled";
const { Item: MenuItem } = Menu;

// Register Size format for Quill (must be done before component renders)
// This ensures the Size format is available when ReactQuill initializes
const Size = Quill.import("formats/size");
Size.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(Size, true);

// Add CSS styles for size classes and size picker labels
if (typeof document !== "undefined") {
  const styleId = "quill-table-size-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      /* Size class styles */
      .ql-size-12px { font-size: 12px !important; }
      .ql-size-14px { font-size: 14px !important; }
      .ql-size-16px { font-size: 16px !important; }
      .ql-size-18px { font-size: 18px !important; }
      .ql-size-24px { font-size: 24px !important; }
      .ql-size-32px { font-size: 32px !important; }
      
      /* Size picker dropdown labels - this fixes the "normal" multiple times issue */
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
        content: '12px';
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
        content: '14px';
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before {
        content: '16px';
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
        content: '18px';
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
        content: '24px';
      }
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before {
        content: '32px';
      }
      
      /* Make toolbar sticky in table cells */
      .customQuill .ql-toolbar {
        position: sticky !important;
        top: 0 !important;
        z-index: 10 !important;
        background: #fff !important;
        border-bottom: 1px solid #e2e8f0 !important;
      }
      
      /* Ensure editor container has proper overflow */
      .customQuill .ql-container {
        max-height: 200px !important;
        overflow-y: auto !important;
        scroll-behavior: smooth !important;
      }
      
      .customQuill .ql-editor {
        max-height: none !important;
        overflow: visible !important;
        padding-bottom: 10px !important;
      }
    `;
    document.head.appendChild(style);
  }
}

// import { useLocation } from "react-router";

export default function QuillTable({ initialColumns, initialData, location }) {
  // const location = useLocation();
  const [preview, setPreview] = useState(window.previewData || false);

  // Ensure Size format is registered with correct whitelist when component mounts
  useEffect(() => {
    try {
      const Size = Quill.import("formats/size");
      // Ensure whitelist is set correctly
      if (
        !Size.whitelist ||
        Size.whitelist.length === 0 ||
        Size.whitelist[0] !== "12px"
      ) {
        Size.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
        Quill.register(Size, true);
        console.log("Size format whitelist updated:", Size.whitelist);
      }
    } catch (e) {
      console.error("Error ensuring Size format is registered:", e);
    }
  }, []);

  // Get pathname from location (handles both React Router location and window.location)
  const pathname =
    location?.pathname ||
    (typeof location === "object" && "pathname" in location
      ? location.pathname
      : window.location?.pathname);

  // Listen for changes to window.previewData
  useEffect(() => {
    const checkPreview = () => {
      const currentPreview = window.previewData || false;
      setPreview(currentPreview);
    };

    // Check immediately
    checkPreview();

    // Set up interval to check for changes (since window.previewData might change externally)
    const interval = setInterval(checkPreview, 100);

    // Also listen for custom event if we dispatch one
    const handlePreviewChange = () => {
      checkPreview();
    };
    window.addEventListener("previewModeChanged", handlePreviewChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener("previewModeChanged", handlePreviewChange);
    };
  }, []);

  // Debug: Log component props and state
  useEffect(() => {
    console.log("QuillTable rendered:", {
      location,
      pathname,
      preview,
      isEditPath: pathname === "/user-guide/edit",
      initialColumns: initialColumns?.length,
      initialData: initialData?.length,
    });
  }, [location, pathname, preview, initialColumns, initialData]);

  const [isFocused, setIsFocused] = useState(false);
  const [isFocusedCell, setIsFocusedCell] = useState(false);
  const tableRef = useRef(null);
  const inputRef = useRef(null);
  const headingRef = useRef(null);
  const isPastingRef = useRef(false);
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on Quill toolbar or its dropdowns
      const isQuillToolbar =
        event.target.closest(".ql-toolbar") ||
        event.target.closest(".ql-picker") ||
        event.target.closest(".ql-picker-options") ||
        event.target.closest(".ql-picker-label") ||
        event.target.closest(".ql-color-picker") ||
        event.target.closest(".ql-align");

      if (
        tableRef.current &&
        !tableRef.current.contains(event.target) &&
        !isQuillToolbar
      ) {
        setEditingCell(null);
        setEditingHeaderIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [data, setData] = useState([
    { key: "1", col0: "", col1: "" },
    { key: "2", col0: "", col1: "" },
  ]);
  const [columns, setColumns] = useState([
    { title: "Column", dataIndex: "col0", key: "col0" },
    { title: "Column", dataIndex: "col1", key: "col1" },
  ]);
  const injectClassToPTags = (html, className) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    let hasPTags = false;
    doc.body.childNodes.forEach((node) => {
      if (node.nodeName.toLowerCase() === "p") {
        hasPTags = true;
      }
    });
    if (!hasPTags) {
      const wrapper = doc.createElement("p");
      wrapper.classList.add(className);
      wrapper.innerHTML = doc.body.innerHTML;
      doc.body.innerHTML = "";
      doc.body.appendChild(wrapper);
    } else {
      const pTags = doc.body.querySelectorAll("p");
      pTags.forEach((p) => p.classList.add(className));
    }
    return doc.body.innerHTML;
  };

  const handleQuillFocus = () => {
    setIsFocusedCell(true);
    const editor = inputRef.current?.getEditor?.();
    if (editor) {
      const editorEl = editor.root;
      editorEl.querySelectorAll("p").forEach((p) => {
        const className = `quillInput${String(editingCell[0])}${String(
          editingCell[1]
        )}`;
        p.classList.add(className);
      });
    }
  };
  const handleQuillFocusHeading = () => {
    setIsFocusedCell(true);
    const editor = headingRef.current?.getEditor?.();
    if (editor) {
      const editorEl = editor.root;
      editorEl.querySelectorAll("p").forEach((p) => {
        p.classList.add(`quillHeading${editingHeaderIndex}`);
      });
    }
  };
  useEffect(() => {
    if (initialColumns?.length) {
      setColumns(initialColumns);
    }
    if (initialData?.length) {
      setData(initialData);
    }
  }, [initialData, initialColumns]);
  const [editingCell, setEditingCell] = useState(null);
  const [editingHeaderIndex, setEditingHeaderIndex] = useState(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });
  const [contextMenuCell, setContextMenuCell] = useState(null); // [rowIndex, colIndex]
  const handleCellChange = (value, rowIndex, colIndex) => {
    const newData = [...data];
    const colKey = columns[colIndex].dataIndex;

    // ALWAYS store Quill value as-is
    newData[rowIndex][colKey] = value;

    setData(newData);
  };

  const extractPlainText = (rawHtml) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = rawHtml;
    const plainText = tempDiv.textContent;
    return plainText;
  };
  const handleHeaderChange = (value, colIndex) => {
    const updatedColumns = [...columns];
    updatedColumns[colIndex].title = value;
    setColumns(updatedColumns);
  };
  const isEmptyTitle = (title) =>
    /^<p(\s+class="[^"]*")?>\s*<br\s*\/?><\/p>$/i.test(title?.trim() || "");
  useEffect(() => {
    if (
      editingHeaderIndex !== null &&
      pathname === "/user-guide/edit" &&
      !preview
    ) {
      const currentCol = columns[editingHeaderIndex];
      if (currentCol && !isEmptyTitle(currentCol.title) && headingRef.current) {
        const editor = headingRef.current.getEditor();
        editor.focus();
        setTimeout(() => {
          const length = editor.getLength();
          editor.setSelection(length - 1, 0);
        }, 0);
      }
    }
  }, [editingHeaderIndex, pathname, preview, columns]);

  useEffect(() => {
    if (
      editingCell &&
      pathname === "/user-guide/edit" &&
      !preview &&
      inputRef.current
    ) {
      const [row, col] = editingCell;
      const currentCellValue = data?.[row]?.[`col${col}`] || "";
      const editor = inputRef.current.getEditor();
      if (currentCellValue.trim()?.length > 0 && editor) {
        const timeout = setTimeout(() => {
          if (!editor.hasFocus()) {
            editor.focus();
            const length = editor.getLength();
            editor.setSelection(length - 1, 0);
          }
        }, 50);
        return () => clearTimeout(timeout);
      }
    }
  }, [editingCell, pathname, preview, data]);

  // Add event listeners to maintain editor state when content changes
  useEffect(() => {
    if (editingCell && pathname === "/user-guide/edit" && !preview && inputRef.current) {
      const editor = inputRef.current.getEditor();
      if (editor && editor.root) {
        // Handle text changes to ensure valid selection and scroll cursor into view
        const handleTextChange = () => {
          try {
            setTimeout(() => {
              const html = editor.root.innerHTML;
              const isEmpty = !html || html.trim() === "" || /^<p><br><\/p>$/.test(html.trim());
              
              // If content is completely empty, add a default whitespace
              if (isEmpty) {
                const currentText = editor.getText();
                if (!currentText || currentText.trim() === '') {
                  editor.insertText(0, ' ', 'user');
                  editor.setSelection(1, 0);
                  return;
                }
              }
              
              const selection = editor.getSelection();
              const length = editor.getLength();
              
              // If no valid selection, set one
              if (!selection || (selection.index === 0 && selection.length === 0 && length > 1)) {
                // Set selection to end
                if (length > 1) {
                  editor.setSelection(length - 1, 0);
                } else {
                  editor.setSelection(0, 0);
                }
              }
              
              // Scroll cursor into view after text change (especially after Enter)
              if (selection) {
                const editorElement = editor.root;
                const container = editorElement?.closest('.ql-container');
                if (container) {
                  // Get the cursor position
                  const bounds = editor.getBounds(selection.index);
                  if (bounds) {
                    const cursorTop = bounds.top;
                    const containerRect = container.getBoundingClientRect();
                    const containerScrollTop = container.scrollTop;
                    const containerHeight = container.clientHeight;
                    
                    // Calculate if cursor is outside visible area
                    const cursorRelativeTop = cursorTop - containerRect.top + containerScrollTop;
                    
                    // If cursor is below visible area, scroll down
                    if (cursorRelativeTop > containerScrollTop + containerHeight - 20) {
                      container.scrollTop = cursorRelativeTop - containerHeight + 40;
                    }
                    // If cursor is above visible area, scroll up
                    else if (cursorRelativeTop < containerScrollTop + 20) {
                      container.scrollTop = cursorRelativeTop - 20;
                    }
                  }
                }
              }
            }, 0);
          } catch (e) {
            console.debug('Error in text change handler:', e);
          }
        };
        
        // Listen for text-change event
        editor.on('text-change', handleTextChange);
        
        const handlePaste = (e) => {
          // Set flag to prevent blur from closing editor during paste
          isPastingRef.current = true;
          // Stop propagation to prevent parent editor from handling paste
          e.stopPropagation();
          // Ensure paste works - don't prevent default (let Quill handle it)
          // Keep the flag set longer to ensure paste completes
          setTimeout(() => {
            isPastingRef.current = false;
          }, 1000);
        };

        const handlePasteCapture = (e) => {
          // Capture paste events early to set the flag and stop propagation
          isPastingRef.current = true;
          // Stop propagation to prevent parent editor from handling paste
          e.stopPropagation();
          // Ensure the cell editor has focus
          if (editor && !editor.hasFocus()) {
            editor.focus();
          }
          setTimeout(() => {
            isPastingRef.current = false;
          }, 1000);
        };

        const handleKeyDown = (e) => {
          // Detect Ctrl+V or Cmd+V (paste keyboard shortcut)
          if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
            // Stop propagation to prevent parent editor from handling paste
            e.stopPropagation();
            // Ensure the cell editor has focus
            if (editor && !editor.hasFocus()) {
              editor.focus();
            }
            isPastingRef.current = true;
            setTimeout(() => {
              isPastingRef.current = false;
            }, 1000);
          }
          
          // Handle Enter key to scroll cursor into view
          if (e.key === 'Enter') {
            setTimeout(() => {
              try {
                const selection = editor.getSelection();
                if (selection) {
                  const editorElement = editor.root;
                  const container = editorElement?.closest('.ql-container');
                  if (container) {
                    // Get the cursor position after Enter
                    const bounds = editor.getBounds(selection.index);
                    if (bounds) {
                      const cursorTop = bounds.top;
                      const containerRect = container.getBoundingClientRect();
                      const containerScrollTop = container.scrollTop;
                      const containerHeight = container.clientHeight;
                      
                      // Calculate cursor position relative to container
                      const cursorRelativeTop = cursorTop - containerRect.top + containerScrollTop;
                      
                      // Scroll to keep cursor visible with some padding
                      if (cursorRelativeTop > containerScrollTop + containerHeight - 30) {
                        container.scrollTop = cursorRelativeTop - containerHeight + 50;
                      } else if (cursorRelativeTop < containerScrollTop + 30) {
                        container.scrollTop = Math.max(0, cursorRelativeTop - 30);
                      }
                    }
                  }
                }
              } catch (e) {
                console.debug('Error scrolling after Enter:', e);
              }
            }, 10);
          }
        };

        // Also add document-level handler to prevent parent editor from handling paste
        const handleDocumentPaste = (e) => {
          // Check if the paste is happening in our table cell editor
          const editorRoot = editor.root;
          if (editorRoot && editorRoot.contains(e.target)) {
            // This paste is for our cell editor, stop it from reaching parent
            e.stopPropagation();
            isPastingRef.current = true;
            setTimeout(() => {
              isPastingRef.current = false;
            }, 1000);
          }
        };

        const editorRoot = editor.root;
        // Add listener in capture phase to catch paste early
        editorRoot.addEventListener('paste', handlePasteCapture, true);
        // Also add in bubble phase
        editorRoot.addEventListener('paste', handlePaste, false);
        // Also listen for keyboard paste shortcuts
        editorRoot.addEventListener('keydown', handleKeyDown, true);
        // Add document-level handler in capture phase to intercept paste before parent editor
        document.addEventListener('paste', handleDocumentPaste, true);
        
        return () => {
          editorRoot.removeEventListener('paste', handlePasteCapture, true);
          editorRoot.removeEventListener('paste', handlePaste, false);
          editorRoot.removeEventListener('keydown', handleKeyDown, true);
          document.removeEventListener('paste', handleDocumentPaste, true);
          // Remove text-change listener
          editor.off('text-change', handleTextChange);
        };
      }
    }
  }, [editingCell, pathname, preview]);

  const contextMenuItems = contextMenuCell ? (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        minWidth: "160px",
        overflow: "hidden",
      }}
    >
      <PaperMachineQualitySelect>
        <Menu
          mode="vertical"
          selectable={false}
          style={{ border: "none", background: "transparent" }}
          onClick={({ key }) => {
            console.log("Menu item clicked:", key, contextMenuCell);
            if (contextMenuCell) {
              handleMenuAction(key, contextMenuCell[0], contextMenuCell[1]);
            }
            setContextMenuVisible(false);
            setContextMenuCell(null);
          }}
        >
          <MenuItem key="insert-row-above" style={{ padding: "0px 10px" }}>
            Insert Row Above
          </MenuItem>
          <MenuItem key="insert-row-below" style={{ padding: "0px 10px" }}>
            Insert Row Below
          </MenuItem>
          <MenuItem key="delete-row" style={{ padding: "0px 10px" }}>
            Delete Row
          </MenuItem>
          <MenuItem key="insert-col-left" style={{ padding: "0px 10px" }}>
            Insert Column Left
          </MenuItem>
          <MenuItem key="insert-col-right" style={{ padding: "0px 10px" }}>
            Insert Column Right
          </MenuItem>
          <MenuItem key="delete-col" style={{ padding: "0px 10px" }}>
            Delete Column
          </MenuItem>
          <MenuItem key="clear-cell" style={{ padding: "0px 10px" }}>
            Clear Cell
          </MenuItem>
        </Menu>
      </PaperMachineQualitySelect>
    </div>
  ) : null;

  // Debug: Log when context menu state changes
  useEffect(() => {
    console.log("Context menu state changed:", {
      visible: contextMenuVisible,
      cell: contextMenuCell,
      position: contextMenuPosition,
    });
  }, [contextMenuVisible, contextMenuCell, contextMenuPosition]);

  const handleMenuAction = (action, rowIndex, colIndex) => {
    // Close any open editor before making changes
    setEditingCell(null);
    setEditingHeaderIndex(null);
    setIsFocusedCell(false);
    
    // Create deep copies to avoid mutation issues
    let newData = data.map(row => ({ ...row }));
    let newCols = columns.map(col => ({ ...col }));
    
    switch (action) {
      case "insert-row-above": {
        const emptyRow = createEmptyRow(newCols);
        newData.splice(rowIndex, 0, emptyRow);
        break;
      }
      case "insert-row-below": {
        const emptyRow = createEmptyRow(newCols);
        newData.splice(rowIndex + 1, 0, emptyRow);
        break;
      }
      case "delete-row":
        if (newData.length > 1) {
          newData.splice(rowIndex, 1);
        }
        break;
      case "insert-col-left":
      case "insert-col-right": {
        const newKey = `col${Date.now()}`;
        const insertAt = action === "insert-col-left" ? colIndex : colIndex + 1;
        newCols.splice(insertAt, 0, {
          title: "Column",
          dataIndex: newKey,
          key: newKey,
        });
        // Add the new column to all existing rows
        newData = newData.map((row) => {
          const newRow = { ...row };
          newRow[newKey] = "";
          return newRow;
        });
        break;
      }
      case "delete-col":
        if (newCols.length > 1) {
          const keyToRemove = newCols[colIndex].dataIndex;
          newCols.splice(colIndex, 1);
          newData = newData.map((row) => {
            const updatedRow = { ...row };
            delete updatedRow[keyToRemove];
            return updatedRow;
          });
        }
        break;
      case "clear-cell": {
        const key = newCols[colIndex].dataIndex;
        if (newData[rowIndex]) {
          newData[rowIndex] = { ...newData[rowIndex] };
          newData[rowIndex][key] = "";
        }
        break;
      }
      default:
        break;
    }
    
    // Update state with new data and columns
    setColumns(newCols);
    setData(newData);
  };

  const createEmptyRow = (cols = columns) => {
    const row = { key: Date.now().toString() };
    cols.forEach((col) => {
      row[col.dataIndex] = "";
    });
    return row;
  };
  const normalizeHtml = (html) => {
    if (!html) return "";
    if (/^<p><br><\/p>$/.test(html.trim())) return "";
    return html;
  };
  const wrappedColumns = columns.map((col, colIndex) => ({
    ...col,
    width: 350,
    title:
      editingHeaderIndex === colIndex &&
      pathname === "/user-guide/edit" &&
      !preview ? (
        <div
          style={{ width: 350 }}
          className={`quillHeading${colIndex}`}
          onKeyDownCapture={(e) => {
            if (e.code === "Enter") {
              e.preventDefault();
            }
          }}
        >
          {!isEmptyTitle(col.title) ? (
            <ReactQuill
              ref={headingRef}
              theme="snow"
              value={col.title}
              onChange={(value) => handleHeaderChange(value, colIndex)}
              onFocus={(colIndex) => {
                setIsFocused(true);
                handleQuillFocusHeading();
              }}
              onBlur={() => {
                setIsFocused(false);
                setEditingHeaderIndex(null);
              }}
              className={"customQuill"}
              modules={{
                toolbar: [["bold", "italic", "underline", "strike"]],
              }}
              formats={["bold", "italic", "underline", "strike"]}
              style={{
                fontFamily: "Manrope",
                border: isFocused ? "2px solid #1677ff" : "1px solid #E2E8F0",
                borderRadius: 4,
                maxHeight: "100px",
                width: "350px",
                overflow: "hidden",
              }}
            />
          ) : (
            <TextArea
              autoSize
              autoFocus
              value={
                !/^<p(\s+class="[^"]*")?>\s*<br\s*\/?><\/p>$/i.test(
                  col?.title?.trim()
                )
                  ? col?.title
                  : ""
              }
              onFocus={() => setIsFocused(true)}
              onChange={(e) => handleHeaderChange(e.target.value, colIndex)}
              onBlur={() => {
                setIsFocused(false);
                setEditingHeaderIndex(null);
              }}
              style={{
                fontFamily: "Manrope",
                border: isFocused ? "2px solid #1677ff" : "1px solid #E2E8F0",
                borderRadius: 4,
                padding: "6px 8px",
                background: "#F8FAFC",
              }}
            />
          )}
        </div>
      ) : extractPlainText(col?.title)?.length > 45 ? (
        <Tooltip title={extractPlainText(col?.title)}>
          <div style={{ width: "350px" }}>
            <div
              style={{
                padding: "4px",
                fontFamily: "Manrope",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "350px",
                cursor:
                  pathname === "/user-guide/edit" && !preview
                    ? "text"
                    : "default",
              }}
              onClick={() => {
                console.log("Column header clicked:", {
                  pathname,
                  preview,
                  canEdit: pathname === "/user-guide/edit" && !preview,
                });
                if (pathname === "/user-guide/edit" && !preview) {
                  setEditingHeaderIndex(colIndex);
                }
              }}
              className={`quillHeading${colIndex}`}
              dangerouslySetInnerHTML={{
                __html: injectClassToPTags(
                  col?.title,
                  `quillHeading${colIndex}`
                ),
              }}
            ></div>
          </div>
        </Tooltip>
      ) : (
        <div
          onClick={() => {
            console.log("Column header clicked:", {
              pathname,
              preview,
              canEdit: pathname === "/user-guide/edit" && !preview,
            });
            if (pathname === "/user-guide/edit" && !preview) {
              setEditingHeaderIndex(colIndex);
            }
          }}
          style={{
            padding: "4px",
            whiteSpace: "normal",
            overflow: "auto",
            maxWidth: "350px",
            fontFamily: "Manrope",
            cursor:
              pathname === "/user-guide/edit" && !preview ? "text" : "default",
          }}
          className={`quillHeading${colIndex}`}
          dangerouslySetInnerHTML={{
            __html: injectClassToPTags(col?.title, `quillHeading${colIndex}`),
          }}
        ></div>
      ),
    render: (_, record, rowIndex) => {
      const isEditing =
        editingCell?.[0] === rowIndex && editingCell?.[1] === colIndex;
      const cellValue = record[col.dataIndex];

      const handleContextMenu = (e) => {
        console.log("Context menu triggered:", {
          pathname,
          preview,
          isEditPath: pathname === "/user-guide/edit",
          rowIndex,
          colIndex,
          clientX: e.clientX,
          clientY: e.clientY,
        });

        // Only show context menu in edit mode and not in preview
        if (pathname === "/user-guide/edit" && !preview) {
          e.preventDefault();
          e.stopPropagation();
          const position = { x: e.clientX, y: e.clientY };
          const cell = [rowIndex, colIndex];
          console.log("Setting context menu:", { position, cell });
          setContextMenuPosition(position);
          setContextMenuCell(cell);
          setContextMenuVisible(true);
          console.log("Context menu visible set to true");
        } else {
          console.log("Context menu blocked - conditions not met:", {
            isEditPath: pathname === "/user-guide/edit",
            isPreview: preview,
            pathname,
          });
        }
      };

      return (
        <div 
          style={{ width: "100%" }} 
          onContextMenu={handleContextMenu}
          onPaste={(e) => {
            // Stop paste from bubbling to parent editor
            if (isEditing && pathname === "/user-guide/edit" && !preview) {
              e.stopPropagation();
              // Ensure the cell editor handles the paste
              const editor = inputRef.current?.getEditor();
              if (editor && !editor.hasFocus()) {
                editor.focus();
              }
            }
          }}
        >
          {isEditing && pathname === "/user-guide/edit" && !preview ? (
            <div 
              style={{ width: "100%" }}
              onPaste={(e) => {
                // Stop paste from bubbling to parent editor
                e.stopPropagation();
              }}
            >
              <ReactQuill
                key={`${rowIndex}-${colIndex}-${
                  editingCell?.[0] === rowIndex && editingCell?.[1] === colIndex
                }`}
                ref={inputRef}
                theme="snow"
                value={cellValue || ""}
                onChange={(value) => {
                  // Check if content is completely empty
                  const isEmpty = !value || value.trim() === "" || /^<p><br><\/p>$/.test(value.trim());
                  
                  const editor = inputRef.current?.getEditor();
                  if (editor && isEmpty) {
                    // If content is empty, add a default whitespace
                    setTimeout(() => {
                      try {
                        const currentContent = editor.root.innerHTML;
                        const isStillEmpty = !currentContent || currentContent.trim() === "" || /^<p><br><\/p>$/.test(currentContent.trim());
                        
                        if (isStillEmpty) {
                          // Insert a space character at the beginning
                          editor.insertText(0, ' ', 'user');
                          // Set cursor after the space
                          editor.setSelection(1, 0);
                          // Update the value with the space
                          const newValue = editor.root.innerHTML;
                          handleCellChange(newValue, rowIndex, colIndex);
                          return; // Don't process further
                        }
                      } catch (e) {
                        console.debug('Error adding default whitespace:', e);
                      }
                    }, 0);
                  }
                  
                  handleCellChange(value, rowIndex, colIndex);
                  
                  // Ensure editor maintains valid state after content changes
                  if (editor) {
                    // If content is empty or just a break, ensure we have a valid structure
                    if (isEmpty) {
                      // Use setTimeout to let Quill process the change first
                      setTimeout(() => {
                        try {
                          const length = editor.getLength();
                          const selection = editor.getSelection();
                          
                          // If no selection or invalid selection, set a valid one
                          if (!selection || selection.length === 0) {
                            // Set selection to the end
                            editor.setSelection(length - 1, 0);
                          }
                          
                          // Ensure editor has focus
                          if (!editor.hasFocus()) {
                            editor.focus();
                          }
                        } catch (e) {
                          console.debug('Error maintaining editor state:', e);
                        }
                      }, 0);
                    }
                  }
                }}
                onFocus={handleQuillFocus}
                onBlur={(previousRange, source, editor) => {
                  // Don't close if the blur was caused by clicking on toolbar dropdowns or pasting
                  setTimeout(() => {
                    // If we're in the middle of a paste operation, don't close
                    if (isPastingRef.current) {
                      // Re-focus the editor to keep it open during paste
                      const editorInstance = inputRef.current?.getEditor();
                      if (editorInstance && !editorInstance.hasFocus()) {
                        setTimeout(() => {
                          editorInstance.focus();
                        }, 10);
                      }
                      return;
                    }
                    
                    const activeElement = document.activeElement;
                    const isToolbarClick = activeElement?.closest('.ql-toolbar') || 
                                          activeElement?.closest('.ql-picker-options') ||
                                          activeElement?.closest('.ql-color-picker') ||
                                          activeElement?.closest('.ql-picker');
                    
                    // Check if editor still has focus
                    const editorInstance = inputRef.current?.getEditor();
                    const hasFocus = editorInstance?.root?.contains(document.activeElement) ||
                                    editorInstance?.hasFocus();
                    
                    if (!isToolbarClick && !hasFocus) {
                      const html = editorInstance?.root?.innerHTML;

                      if (/^<p><br><\/p>$/.test(html?.trim())) {
                        handleCellChange("", rowIndex, colIndex);
                      }

                      setEditingCell(null);
                      setIsFocusedCell(false);
                    }
                  }, 300);
                }}
                className="customQuill"
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline", "strike"],
                    [
                      {
                        size: ["12px", "14px", "16px", "18px", "24px", "32px"],
                      },
                    ],
                    [{ color: [] }],
                    [{ align: [] }],
                  ],
                  clipboard: {
                    // Allow paste to work normally
                    matchVisual: false,
                  },
                }}
                formats={[
                  "bold",
                  "italic",
                  "underline",
                  "strike",
                  "size",
                  "color",
                  "align",
                ]}
                style={{
                  fontFamily: "Manrope",
                  border: isFocusedCell
                    ? "2px solid #1677ff"
                    : "1px solid #E2E8F0",
                  borderRadius: 4,
                  maxHeight: "300px",
                  width: "100%",
                  overflow: "visible",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                }}
              />
            </div>
          ) : (
            <div
              style={{
                padding: "4px",
                whiteSpace: "normal",
                overflow: "hidden",
                maxWidth: "350px",
                fontFamily: "Manrope",
                cursor: "text",
                minHeight: "20px",
                wordBreak: "break-word",
              }}
              onClick={() => {
                if (pathname === "/user-guide/edit" && !preview) {
                  setEditingCell([rowIndex, colIndex]);
                }
              }}
              dangerouslySetInnerHTML={{
                __html: injectClassToPTags(
                  normalizeHtml(cellValue),
                  `quillInput${rowIndex}${colIndex}`
                ),
              }}
            />
          )}
        </div>
      );
    },
  }));

  // Handle click outside to close context menu and prevent default context menu on table
  const contextMenuTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuVisible) {
        // Don't close if clicking on the menu itself
        const isMenuClick =
          event.target.closest(".ant-menu") ||
          event.target.closest(".ant-dropdown-menu") ||
          event.target.closest("[data-context-menu]");
        if (!isMenuClick) {
          console.log("Click outside detected, closing menu");
          setContextMenuVisible(false);
          setContextMenuCell(null);
        }
      }
    };

    const handleContextMenu = (event) => {
      // Prevent default context menu on table container in edit mode
      if (pathname === "/user-guide/edit" && !preview) {
        const isTableCell =
          event.target.closest("td") || event.target.closest(".ant-table-cell");
        if (!isTableCell) {
          event.preventDefault();
        }
      }
    };

    // Use a small delay to prevent immediate closing when right-click opens the menu
    if (contextMenuVisible) {
      contextMenuTimeoutRef.current = setTimeout(() => {
        document.addEventListener("click", handleClickOutside, true);
      }, 100);
    }

    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      if (contextMenuTimeoutRef.current) {
        clearTimeout(contextMenuTimeoutRef.current);
      }
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [contextMenuVisible, pathname, preview]);

  return (
    <div ref={tableRef}>
      <Table
        dataSource={data}
        columns={wrappedColumns}
        pagination={false}
        bordered
        className={styles.table_wrapper}
        style={{
          marginBottom: "12px",
          overflow: "auto",
          maxWidth: "90vw",
          minHeight: "auto",
        }}
      />
      {contextMenuVisible && contextMenuItems && (
        <div
          data-context-menu="true"
          ref={(el) => {
            if (el) {
              console.log("Context menu rendered in DOM:", {
                element: el,
                position: contextMenuPosition,
                visible: contextMenuVisible,
                hasItems: !!contextMenuItems,
                computedStyle: window.getComputedStyle(el),
                boundingRect: el.getBoundingClientRect(),
              });
            }
          }}
          style={{
            position: "fixed",
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
            zIndex: 9999,
            pointerEvents: "auto",
          }}
          onClick={(e) => {
            console.log("Context menu container clicked");
            e.stopPropagation();
          }}
          onContextMenu={(e) => {
            console.log("Context menu on menu container");
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {contextMenuItems}
        </div>
      )}
    </div>
  );
}
